import clsx from 'clsx';
import React from 'react';
import { type RadioAria, useRadio, VisuallyHidden } from 'react-aria';
import type { SlotProps } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { assert } from '@gitbook/util-ts';

import { useRadioGroupContext } from './RadioGroup';

export interface RadioProps extends SlotProps {
    /**
     * The value of the radio.
     */
    value: string;

    /**
     * If true, the specific radio value is disabled.
     * `disabled` can also be used to disable the entire radio group on the `RadioGroup` component.
     * @default false
     */
    disabled?: boolean;

    /**
     * The class name of the radio.
     */
    className?: string;

    /**
     * Content of the radio.
     */
    children: React.ReactNode;
}

export type RadioRef = HTMLLabelElement;

const RadioContext = React.createContext<RadioAria | null>(null);

/**
 * Radio component for forms with built-in accessibility support.
 * @example
 * <RadioGroup>
 *   <Radio value="1">
 *    <RadioCheck />
 *    <Label>Option 1</Label>
 *   </Radio>
 * </Radio>
 * </RadioGroup>
 */
export const Radio = React.forwardRef<RadioRef, RadioProps>((props, ref) => {
    const { value, disabled = false, className, children, ...rest } = props;
    const radioGroup = useRadioGroupContext();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const radio = useRadio(
        {
            value,
            isDisabled: disabled,
            ...rest,
        },
        radioGroup,
        inputRef
    );

    return (
        <RadioContext.Provider value={radio}>
            <label ref={ref} {...radio.labelProps} className={className}>
                <VisuallyHidden>
                    <input {...radio.inputProps} ref={inputRef} />
                </VisuallyHidden>
                {children}
            </label>
        </RadioContext.Provider>
    );
});

export type RadioCheckProps = {
    /**
     * The size of the radio.
     */
    size?: 'small' | 'medium';
};

/**
 * Visual representation of a radio button.
 */
export function RadioCheck(props: RadioCheckProps) {
    const { size = 'medium' } = props;
    const radioGroup = useRadioGroupContext();
    const radio = React.useContext(RadioContext);
    assert(radio, 'RadioCheck must be used within a Radio');

    return (
        <div
            className={clsx(
                'flex shrink-0 items-center justify-center rounded-full border data-[focus-visible]:shadow-focus-ring',
                { small: 'size-4', medium: 'size-5' }[size],
                radio.isDisabled
                    ? 'cursor-not-allowed opacity-disabled'
                    : radioGroup.isReadOnly
                      ? 'cursor-default'
                      : 'cursor-pointer',
                radio.isSelected
                    ? 'border-primary bg-primary text-white'
                    : radio.isDisabled
                      ? 'border-[var(--sp-color-border-form-radio-unchecked-disabled)] bg-inactive'
                      : 'border-gray-300 bg-base'
            )}
        >
            <Icon.AnimatedCheck isSelected={radio.isSelected} />
        </div>
    );
}
